import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Select,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { CustomerAnalyticsStyles } from "./CustomerAnalyticsStyles";
import { useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import CustomSkeleton from "../../common/CustomSkeleton/CustomSkeleton";
import CustomPieChart from "../components/PieChart/PieChart";
import {
  getPieChartData,
  getPieChartTitle,
  getPieChartColors,
} from "./CustomerAnalyticsUtils";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { ConversationAnalysisStyles } from "../ConversationAnalysis/ConversationAnalysisStyles";

const formatDate = (dateString: string): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  return new Intl.DateTimeFormat("en-US", options).format(date);
};

export interface ISessionListProps {
  selectedAnalyticsType: number;
  statusFilter: string;
  lobFilter: string;
  selectedSession: { customer: string; sessionId: string } | null;
  setSelectedSession: React.Dispatch<
    React.SetStateAction<{ customer: string; sessionId: string } | null>
  >;
}

const SessionList: React.FC<ISessionListProps> = ({
  selectedAnalyticsType,
  statusFilter,
  lobFilter,
  selectedSession,
  setSelectedSession,
}) => {
  const { customerActivityGroup } = useAppSelector(
    (state: RootState) => state.customerAnalytics
  );
  const { isLoading } = useAppSelector((state: RootState) => state.common);
  const isMobile = useMediaQuery("(max-width:767px)");

  const filteredCustomerActivityGroup = React.useMemo(() => {
    let filtered: typeof customerActivityGroup = {};

    // First apply status filter
    if (statusFilter === "All") {
      filtered = customerActivityGroup;
    } else {
      customerActivityGroup &&
        Object.entries(customerActivityGroup).forEach(
          ([customerName, sessions]) => {
            const filteredSessions: { [key: string]: any[] } = {};
            Object.entries(sessions).forEach(([sessionId, activities]) => {
              const filteredActivities = activities.filter(
                (activity) =>
                  activity.status?.toLowerCase() === statusFilter.toLowerCase()
              );
              if (filteredActivities.length > 0) {
                filteredSessions[sessionId] = filteredActivities;
              }
            });
            if (Object.keys(filteredSessions).length > 0) {
              filtered[customerName] = filteredSessions;
            }
          }
        );
    }

    // Apply LOB filter if in policy tab
    if (selectedAnalyticsType === 1 && lobFilter !== "All") {
      const lobFiltered: typeof customerActivityGroup = {};
      filtered &&
        Object.entries(filtered).forEach(([customerName, sessions]) => {
          const filteredSessions: { [key: string]: any[] } = {};
          Object.entries(sessions).forEach(([sessionId, activities]) => {
            const filteredActivities = activities.filter(
              (activity) => activity.lobCode === lobFilter
            );
            if (filteredActivities.length > 0) {
              filteredSessions[sessionId] = filteredActivities;
            }
          });
          if (Object.keys(filteredSessions).length > 0) {
            lobFiltered[customerName] = filteredSessions;
          }
        });
      return lobFiltered;
    }

    return filtered;
  }, [
    customerActivityGroup,
    statusFilter,
    lobFilter,
    selectedAnalyticsType,
    selectedSession,
  ]);

  // Get filtered activities for pie chart when a session is selected
  const getFilteredActivitiesForSelectedSession = (activities: any[]) => {
    if (!selectedSession || !activities) return activities;

    if (selectedAnalyticsType === 1 && lobFilter !== "All") {
      return activities.filter((activity) => activity.lobCode === lobFilter);
    }

    return activities;
  };

  // Get filtered sessions for a customer based on LOB and status
  const getFilteredCustomerSessions = (customerName: string) => {
    const sessions = customerActivityGroup[customerName] || {};
    const filteredSessions =
      sessions &&
      Object.entries(sessions)
        .map(([sessionId, activities]) => {
          // Filter activities based on LOB and status
          const filteredActivities = activities.filter((activity) => {
            const matchesStatus =
              statusFilter === "All" ||
              activity.status?.toLowerCase() === statusFilter.toLowerCase();
            const matchesLOB =
              selectedAnalyticsType !== 1 ||
              lobFilter === "All" ||
              activity.lobCode === lobFilter;
            return matchesStatus && matchesLOB;
          });

          if (filteredActivities.length > 0) {
            return {
              sessionId,
              activities: filteredActivities,
              startTime: filteredActivities[0]?.createdAt || "",
              endTime:
                filteredActivities[filteredActivities.length - 1]?.createdAt ||
                "",
            };
          }
          return null;
        })
        .filter(
          (
            session
          ): session is {
            sessionId: string;
            activities: any[];
            startTime: string;
            endTime: string;
          } => session !== null
        );

    return filteredSessions.sort(
      (a, b) =>
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
    );
  };

  // Reset session selection if current session doesn't match filters
  React.useEffect(() => {
    if (selectedSession) {
      const filteredSessions = getFilteredCustomerSessions(
        selectedSession.customer
      );
      if (
        filteredSessions.length === 0 ||
        !filteredSessions.some(
          (session) => session.sessionId === selectedSession.sessionId
        )
      ) {
        setSelectedSession({
          customer: selectedSession.customer,
          sessionId: "",
        });
      }
    }
  }, [lobFilter, statusFilter, selectedAnalyticsType]);

  // Get all customers with their date ranges
  const getCustomerList = useMemo(() => {
    const customers: Array<{
      customerName: string;
      startTime: string;
      endTime: string;
      sessions: Array<{
        sessionId: string;
        activities: any[];
        startTime: string;
        endTime: string;
      }>;
    }> = [];

    customerActivityGroup &&
      Object.entries(customerActivityGroup)?.forEach(
        ([customerName, sessions]) => {
          const allSessions = Object.entries(sessions || {})
            ?.map(([sessionId, activities]) => ({
              sessionId,
              activities,
              startTime: activities[0]?.createdAt || "",
              endTime: activities[activities.length - 1]?.createdAt || "",
            }))
            .sort(
              (a, b) =>
                new Date(a.startTime).getTime() -
                new Date(b.startTime).getTime()
            );

          if (allSessions.length > 0) {
            const firstSession = allSessions[0];
            const lastSession = allSessions[allSessions.length - 1];
            if (firstSession && lastSession) {
              customers.push({
                customerName,
                startTime: firstSession.startTime,
                endTime: lastSession.endTime,
                sessions: getFilteredCustomerSessions(customerName),
              });
            }
          }
        }
      );

    return customers;
  }, [customerActivityGroup, statusFilter, lobFilter, selectedAnalyticsType]);

  // Add this function near the top of the component
  const getSelectedSessionTimes = (
    selectedSession: { customer: string; sessionId: string } | null
  ) => {
    if (!selectedSession || !selectedSession.sessionId)
      return { startTime: "", endTime: "" };

    const sessions =
      customerActivityGroup[selectedSession.customer]?.[
        selectedSession.sessionId
      ] || [];
    if (sessions.length === 0) return { startTime: "", endTime: "" };

    return {
      startTime: sessions[0]?.loginTime || "",
      endTime: sessions[sessions.length - 1]?.logoutTime || "",
    };
  };

  return (
    <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
      <Box
        sx={{
          ...CustomerAnalyticsStyles.sessionListContainer,
          width: isMobile ? "100%" : "40vw",
          maxWidth: isMobile ? "calc(100vw - 40px)" : "none",
        }}
      >
        <Box sx={CustomerAnalyticsStyles.sessionsHeadingContainer}>
          <Typography padding="1rem" fontWeight="bold">
            Select Customer
          </Typography>
        </Box>
        <Divider />
        <Box sx={CustomerAnalyticsStyles.sessionList}>
          {isLoading ? (
            <CustomSkeleton count={4} direction="column" height="120px" />
          ) : getCustomerList.length === 0 ? (
            <Typography textAlign="center" fontWeight="bold" marginTop="2rem">
              No Customer History available.
            </Typography>
          ) : (
            getCustomerList.map(
              ({ customerName, startTime, endTime, sessions }) => (
                <Paper
                  key={customerName}
                  sx={{
                    ...CustomerAnalyticsStyles.sessionCard,
                    border:
                      selectedSession?.customer === customerName
                        ? "2px solid #7C2AE8"
                        : undefined,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const newSession = {
                      customer: customerName,
                      sessionId: "",
                    };
                    setSelectedSession(newSession);
                  }}
                >
                  <Typography sx={{ fontSize: "0.9rem", fontStyle: "italic" }}>
                    Customer:
                    <span style={{ fontWeight: "normal", fontSize: "1rem" }}>
                      {" "}
                      {customerName}
                    </span>
                  </Typography>
                  <Box
                    sx={ConversationAnalysisStyles.sessionDatesContianer}
                    id={`session_details_dates_${customerName}`}
                    data-test-id={`session_details_dates_${customerName}`}
                    aria-label="Session Time Period"
                  >
                    <Box
                      sx={ConversationAnalysisStyles.sessionDateAndIcon}
                      id={`session_details_start_time_${customerName}`}
                      data-test-id={`session_details_start_time_${customerName}`}
                      aria-label={`Session Start Time: ${
                        formatDate(startTime) || "NA"
                      }`}
                    >
                      {"From"}
                      <AccessTimeOutlinedIcon fontSize="small" />
                      <Typography sx={{ fontSize: "14px" }}>
                        {formatDate(startTime) || "NA"}
                      </Typography>
                    </Box>
                    <Box
                      sx={ConversationAnalysisStyles.sessionDateAndIcon}
                      id={`session_details_end_time_${customerName}`}
                      data-test-id={`session_details_end_time_${customerName}`}
                      aria-label={`Session End Time: ${
                        formatDate(endTime) || "NA"
                      }`}
                    >
                      {"To"}
                      <AccessTimeOutlinedIcon fontSize="small" />
                      <Typography sx={{ fontSize: "14px" }}>
                        {formatDate(endTime) || "NA"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )
            )
          )}
        </Box>
      </Box>
      <Box sx={CustomerAnalyticsStyles.pieChartContainer}>
        <Box sx={CustomerAnalyticsStyles.PieChartHeadingContainer}>
          <Box
            sx={{
              padding: "0.5rem 1rem",
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Select
                size="small"
                value={selectedSession?.sessionId || ""}
                onChange={(e) => {
                  const sessionId = e.target.value;
                  if (selectedSession) {
                    setSelectedSession({ ...selectedSession, sessionId });
                  }
                }}
                displayEmpty
                disabled={
                  !selectedSession ||
                  getFilteredCustomerSessions(selectedSession.customer)
                    .length === 0
                }
                id="Request_selector"
                data-test-id="Request_selector"
                aria-label="Select Session ID"
                sx={{ minWidth: "200px" }}
              >
                <MenuItem
                  value=""
                  disabled
                  id="Request_selector_placeholder"
                  data-test-id="Request_selector_placeholder"
                  aria-label="Select Session ID"
                >
                  Select Session ID
                </MenuItem>
                {selectedSession &&
                  getFilteredCustomerSessions(selectedSession.customer).map(
                    ({ sessionId }, index) => (
                      <MenuItem
                        key={sessionId}
                        value={sessionId}
                        id={`session_option_${sessionId}`}
                        data-test-id={`session_option_${sessionId}`}
                        aria-label={`Session ${index + 1}`}
                      >
                        {`Session ${index + 1}`}
                      </MenuItem>
                    )
                  )}
              </Select>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {selectedSession && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.85rem",
                      fontStyle: "italic",
                    }}
                  >
                    {getFilteredCustomerSessions(selectedSession.customer)
                      .length === 0
                      ? "No sessions found for selected filters"
                      : selectedSession.sessionId
                      ? `Selected Session ID: ${selectedSession.sessionId}`
                      : "Select a session"}
                  </Typography>
                )}
                {selectedSession && selectedSession.sessionId && (
                  <Box
                    sx={{
                      ...ConversationAnalysisStyles.sessionDatesContianer,
                      gap: "1rem",
                    }}
                  >
                    <Box sx={ConversationAnalysisStyles.sessionDateAndIcon}>
                      <Typography sx={{ fontSize: "14px" }}>
                        From:{" "}
                        {formatDate(
                          getSelectedSessionTimes(selectedSession).startTime
                        )}
                      </Typography>
                    </Box>
                    <Box sx={ConversationAnalysisStyles.sessionDateAndIcon}>
                      <Typography sx={{ fontSize: "14px" }}>
                        To:{" "}
                        {formatDate(
                          getSelectedSessionTimes(selectedSession).endTime
                        )}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <CustomPieChart
          title={getPieChartTitle(selectedAnalyticsType)}
          data={(() => {
            // Add this check
            if (!selectedSession?.customer) {
              return getPieChartData(
                filteredCustomerActivityGroup,
                selectedAnalyticsType
              );
            }

            // If customer is selected but no session
            if (selectedSession.customer && !selectedSession.sessionId) {
              return getPieChartData(
                {
                  [selectedSession.customer]:
                    filteredCustomerActivityGroup[selectedSession.customer],
                },
                selectedAnalyticsType
              );
            }
            return getPieChartData(
              {
                [selectedSession.customer]: {
                  [selectedSession.sessionId]:
                    getFilteredActivitiesForSelectedSession(
                      filteredCustomerActivityGroup[selectedSession.customer]?.[
                        selectedSession.sessionId
                      ] || []
                    ),
                },
              },
              selectedAnalyticsType
            );
          })()}
          colors={getPieChartColors(selectedAnalyticsType)}
        />
      </Box>
    </Box>
  );
};

export default SessionList;
